// Core packages
import { CollectionViewer } from '@angular/cdk/collections';
import { DataSource } from '@angular/cdk/table';

// Third party packages
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

// Custom packages
import { PropertiesService } from 'src/app/modules/properties/properties.service';
import { Property } from 'src/app/shared/models/property/property.model';
import { HelperService } from 'src/app/shared/services/helper.service';

/**
 * @see https://blog.angular-university.io/angular-material-data-table/
 *
 * NB
 * If you need to change this to make a new DataSource
 * just change the following:
 *
 * - Change The class name in the first line
 * - Change DataSource<T> (use your model type) in first line
 * - Change BehaviourSubject<T> in the second line
 * - Change the service inside the class constructor
 * - Change the Observable<T> used in the connect() line
 * - Change the service name inside loadItems()
 */
export class PropertiesDataSource implements DataSource<Property> {
  public itemsSubject$ = new BehaviorSubject<Property[]>([]);
  private totalCountSubject$ = new BehaviorSubject<number>(0);
  private loadingSubject$ = new BehaviorSubject<boolean>(true);

  public loading$ = this.loadingSubject$.asObservable();
  public totalCount$ = this.totalCountSubject$.asObservable();

  public itemsLength = 0;
  public items: Property[] = [];

  /**
   * Class constructor
   */
  constructor(
    private propertiesService: PropertiesService,
    private helperService: HelperService
  ) { }

  /**
   * Connect data table to itemsSubject$
   *
   * @since 1.0.0
   *
   * @param collectionViewer collection viewer object
   * @returns Observable
   */
  connect(collectionViewer: CollectionViewer): Observable<Property[]> {
    return this.itemsSubject$.asObservable();
  }

  /**
   * Disconnect data table from subjects
   *
   * @since 1.0.0
   *
   * @param collectionViewer collection viewer object
   */
  disconnect(collectionViewer: CollectionViewer): void {
    this.itemsSubject$.complete();
    this.totalCountSubject$.complete();
    this.loadingSubject$.complete();
  }

  /**
   * Load items from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   */
  loadItems(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any, suspended?: boolean): void {
    this.loadingSubject$.next(true); // Start loading
    if (!suspended) {
      this.propertiesService.getList(start, limit, sort, sortVersus, search)
        .pipe(
          catchError(err => { // If errors, return empty array
            this.helperService.handleError(err);
            return of({ status: true, data: { items: [], totalCount: 0 } });
          }),
          finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
        )
        .subscribe((result: any) => {
          if (result.data.items && Array.isArray(result.data.items)) {
            result.data.items.forEach((el: any) => {
              el.status = "";
              if (el.propertyStatus) {
                if (
                  el.propertyStatus.ceased &&
                  el.propertyStatus.ceased.checked
                ) {
                  el.status = "CESSATO";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.partialMapping &&
                  el.propertyStatus.partialMapping.checked
                ) {
                  el.status = "MAPPATURA\nPARZIALE";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.mappingConfirmed &&
                  el.propertyStatus.mappingConfirmed.otherActivity
                ) {
                  el.status = "MAPPATURA\nCOMPLETA";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.insertionCompletedPartial &&
                  el.propertyStatus.insertionCompletedPartial.checked
                ) {
                  el.status = "INSERIMENTO\nPARZIALE TERMINATO";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.insertionCompleted &&
                  el.propertyStatus.insertionCompleted.checked
                ) {
                  el.status = "INSERIMENTO\nTERMINATO";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.insertionConfirmedPartial &&
                  el.propertyStatus.insertionConfirmedPartial.checked
                ) {
                  el.status = "INSERIMENTO\nPARZIALE CONFERMATO";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.mappingCompleted &&
                  el.propertyStatus.mappingCompleted.checked
                ) {
                  el.status = "MAPPATURA\nTERMINATA";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.insertionConfirmed &&
                  el.propertyStatus.insertionConfirmed.checked
                ) {
                  el.status = "INSERIMENTO\nCONFERMATO";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.mappingConfirmed &&
                  el.propertyStatus.mappingConfirmed.checked
                ) {
                  el.status = "MAPPATURA\nCONFERMATA";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.sag &&
                  el.propertyStatus.sag.checked
                ) {
                  el.status = "ANAGRAFICA";
                  el.subscriptionEndDate = "----";
                } else {
                  el.status = "";
                  el.subscriptionEndDate = "";
                }

                if (
                  el.propertyStatus.startDate &&
                  el.propertyStatus.startDate.checked
                ) {
                  el.subscriptionEndDate = el.subscription && el.subscription.endDate ? el.subscription.endDate : '';
                }
              }
            });
          }
          this.itemsSubject$.next(result.data.items);
          this.totalCountSubject$.next(result.data.totalCount);

          this.itemsLength = result.data.items.length;
          this.items = result.data.items;
        });
    } else {
      this.propertiesService.getSuspendedList(start, limit, sort, sortVersus, search)
        .pipe(
          catchError(err => { // If errors, return empty array
            this.helperService.handleError(err);
            return of({ status: true, data: { items: [], totalCount: 0 } });
          }),
          finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
        )
        .subscribe((result: any) => {
          if (result.data.items && Array.isArray(result.data.items)) {
            result.data.items.forEach((el: any) => {
              el.status = "";
              if (el.propertyStatus) {
                if (
                  el.propertyStatus.ceased &&
                  el.propertyStatus.ceased.checked
                ) {
                  el.status = "CESSATO";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.partialMapping &&
                  el.propertyStatus.partialMapping.checked
                ) {
                  el.status = "MAPPATURA\nPARZIALE";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.mappingConfirmed &&
                  el.propertyStatus.mappingConfirmed.otherActivity
                ) {
                  el.status = "MAPPATURA\nCOMPLETA";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.insertionCompletedPartial &&
                  el.propertyStatus.insertionCompletedPartial.checked
                ) {
                  el.status = "INSERIMENTO\nPARZIALE TERMINATO";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.insertionCompleted &&
                  el.propertyStatus.insertionCompleted.checked
                ) {
                  el.status = "INSERIMENTO\nTERMINATO";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.insertionConfirmedPartial &&
                  el.propertyStatus.insertionConfirmedPartial.checked
                ) {
                  el.status = "INSERIMENTO\nPARZIALE CONFERMATO";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.mappingCompleted &&
                  el.propertyStatus.mappingCompleted.checked
                ) {
                  el.status = "MAPPATURA\nTERMINATA";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.insertionConfirmed &&
                  el.propertyStatus.insertionConfirmed.checked
                ) {
                  el.status = "INSERIMENTO\nCONFERMATO";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.mappingConfirmed &&
                  el.propertyStatus.mappingConfirmed.checked
                ) {
                  el.status = "MAPPATURA\nCONFERMATA";
                  el.subscriptionEndDate = "----";
                } else if (
                  el.propertyStatus.sag &&
                  el.propertyStatus.sag.checked
                ) {
                  el.status = "ANAGRAFICA";
                  el.subscriptionEndDate = "----";
                } else {
                  el.status = "";
                  el.subscriptionEndDate = "";
                }

                if (
                  el.propertyStatus.startDate &&
                  el.propertyStatus.startDate.checked
                ) {
                  el.subscriptionEndDate = el.subscription && el.subscription.endDate ? el.subscription.endDate : '';
                }
              }
            });
          }
          this.itemsSubject$.next(result.data.items);
          this.totalCountSubject$.next(result.data.totalCount);

          this.itemsLength = result.data.items.length;
          this.items = result.data.items;
        });
    }
  }

  /**
   * Load items from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   */
  loadTagNfcItems(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): void {
    this.loadingSubject$.next(true); // Start loading

    this.propertiesService.getTagNfcList(start, limit, sort, sortVersus, search)
      .pipe(
        catchError(err => { // If errors, return empty array
          this.helperService.handleError(err);
          return of({ status: true, data: { items: [], totalCount: 0 } });
        }),
        finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
      )
      .subscribe((result: any) => {
        this.itemsSubject$.next(result.data.items);
        this.totalCountSubject$.next(result.data.totalCount);

        this.itemsLength = result.data.items.length;
        this.items = result.data.items;
      });
  }

  /**
   * Load items from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   */
  loadInStructureItems(topicKey: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): void {
    this.loadingSubject$.next(true); // Start loading
    this.propertiesService.getInStructureList(topicKey, start, limit, sort, sortVersus, search)
      .pipe(
        catchError(err => { // If errors, return empty array
          this.helperService.handleError(err);
          return of({ status: true, data: { items: [], totalCount: 0 } });
        }),
        finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
      )
      .subscribe((result: any) => {
        this.itemsSubject$.next(result.data.items);
        this.totalCountSubject$.next(result.data.totalCount);

        this.itemsLength = result.data.items.length;
        this.items = result.data.items;
      });
  }
}
